import { Box, Flex, Heading, Link, List, ListItem, Skeleton, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { DetailSheet } from '../../../components/DetailSheet'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import PreviewModal from '../../../components/previewModal'
import { Tooltip } from '../../../components/Tooltip'
import { formatApiDate, formatDateTime, formatFileSize, formatPhone } from '../../../helpers'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink, ucFirst } from './helper'
import { EEntregaDeTrabalhoType, IEntregaDeTrabalhoComplete, IEntregaDeTrabalhoUpload } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  id?: number
}

export function EntregaDeTrabalhoViewModal({ isOpen, onClose, id }: IProps) {
  const service = new EntregaDeTrabalhoService()
  const [loading, setLoading] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workSubmission, setWorkSubmission] = useState<IEntregaDeTrabalhoComplete>({} as any)
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })

  useEffect(() => {
    if (!id || !isOpen) return

    setLoading(true)
    service
      .get(id)
      .then((response) => {
        setWorkSubmission(response)
      })
      .finally(() => setLoading(false))
  }, [isOpen])

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  const review = workSubmission.review

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <Skeleton isLoaded={!loading} width="50%">
            {EEntregaDeTrabalhoType[workSubmission.type] || 'Entrega de trabalho'}
          </Skeleton>
        }
        size="wide"
      >
        <DetailSheet
          isLoading={loading}
          fields={[
            { label: 'Status', value: ucFirst(workSubmission.status) },
            { label: 'Nome do Aluno', value: workSubmission.name },
            { label: 'CPF do Aluno', value: workSubmission.cpf },
            { label: 'Nome do Responsável', value: workSubmission.responsable_name },
            { label: 'CPF do Responsável', value: workSubmission.responsable_cpf },
            { label: 'E-mail', value: workSubmission.email },
            { label: 'Telefone', value: formatPhone(workSubmission.phone) },
            { label: 'Curso', value: workSubmission.course },
            {
              label: 'Modalidade',
              value: courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo']
            },
            { label: 'Professor', value: workSubmission.teacher },
            { label: 'Dias de aula', value: workSubmission['class-days'] },
            {
              label: 'Horário das aulas',
              value:
                workSubmission['class-hour-start'] &&
                `${workSubmission['class-hour-start'].substring(0, 5)} às ${workSubmission[
                  'class-hour-ends'
                ]?.substring(0, 5)}`
            },
            {
              label: 'Data de submissão',
              value: workSubmission.created_at && formatDateTime(workSubmission.created_at)
            },
            {
              label: 'Arquivos',
              value: workSubmission.uploads?.length ? (
                <List>
                  {workSubmission.uploads.map((upload) => (
                    <ListItem key={upload.id}>
                      <Flex mb={2} gap={3} align="center" justify="flex-end">
                        <Box>
                          {upload.original_name}{' '}
                          <Text variant="caption" as="span">
                            ({formatFileSize(Number(upload.size))})
                          </Text>
                        </Box>
                        <Tooltip label="Abrir preview">
                          <Link color="blue.500" onClick={() => handleOpenPreview(upload)}>
                            <Icon name="lni lni-eye" label="Abrir preview" />
                          </Link>
                        </Tooltip>
                        <Tooltip label="Fazer download">
                          <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color="blue.500">
                            <Icon name="lni lni-download" label="Fazer download" />
                          </Link>
                        </Tooltip>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
              ) : null
            }
          ]}
        />

        {review && (
          <>
            <Heading as="h3" mt={6} mb={2}>
              Avaliação
            </Heading>
            <DetailSheet
              fields={[
                { label: 'Professor', value: review.teacher },
                review.start_date &&
                  review.end_date && { label: 'Data de Início', value: formatApiDate(review.start_date) },
                review.start_date &&
                  review.end_date && { label: 'Data de Término', value: formatApiDate(review.end_date) },
                review.course_load && review.contract_number && { label: 'Carga Horária', value: review.course_load },
                review.course_load &&
                  review.contract_number && { label: 'Nº do Contrato', value: review.contract_number },
                review.class_code && { label: 'Cód. Turma', value: review.class_code },
                { label: 'Nota', value: Number(review.grade).toFixed(2) },
                { label: 'Comentário', value: review.observations }
              ]}
            />
          </>
        )}
      </FormModal>

      <PreviewModal
        isOpen={previewModal}
        onClose={() => {
          setPreviewModal(false)
        }}
        url={previewData.url}
        fileName={previewData.fileName}
        fileSize={previewData.fileSize}
      />
    </>
  )
}
